<template>
  <div
    v-if="value"
    class="remove-badge-dialog-overlay"
    @click.self="handleOverlayClick"
  >
    <div class="remove-badge-dialog">
      <!-- Modal Header -->
      <div class="modal-header">
        <div class="header-content">
          <div class="featured-icon">
            <Icon name="removed_icon_gray" />
          </div>
          <div class="text-content">
            <h2 class="dialog-title">Remove Badge</h2>
          </div>
        </div>
        <button
          class="close-button"
          @click="handleClose"
          aria-label="Close dialog"
        >
          <Icon name="interface_close" :size="24" />
        </button>
      </div>

      <!-- Content -->
      <div class="dialog-content">
        <p class="content-text">
          You are about to remove the {{ badgeName || "Apprentice" }} badge for
          responsibility
          {{ responsibilityName || "Communicate Internally Effectively" }} from
          employee {{ employeeName || "Olivia Rhye" }}. You need to provide a
          Reason for it.
        </p>
        <p class="content-text warning-text">This operation is irreversible</p>
      </div>

      <!-- Reason Input -->
      <div class="reason-input-section" :class="{ 'input-dirty': isDirty }">
        <MemoInputWithLimit
          v-model="reason"
          label="Reason"
          placeholder="Enter reason for removal"
          :required="true"
          :min="3"
          :max="275"
          :status="reasonStatus"
          :show-char-counter="false"
          @input="handleReasonInput"
        />
      </div>

      <!-- Modal Actions -->
      <div class="modal-actions">
        <div class="actions-content">
          <div class="actions-right">
            <StandardButton
              type="terminated"
              variant="primary"
              size="normal"
              label="Remove"
              :disabled="!isValid"
              @click="handleRemove"
              class="remove-button-full-width"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import Icon from "@components/icons/Icon.vue";
import MemoInputWithLimit from "@components/MemoInputWithLimit/MemoInputWithLimit.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";

const props = defineProps({
  value: {
    type: Boolean,
    default: false,
  },
  badgeName: {
    type: String,
    default: "Apprentice",
  },
  responsibilityName: {
    type: String,
    default: "Communicate Internally Effectively",
  },
  employeeName: {
    type: String,
    default: "Olivia Rhye",
  },
});

const emit = defineEmits(["input", "remove", "close"]);

const reason = ref("");
const reasonStatus = ref("Not focused");

const isValid = computed(() => {
  return reason.value && reason.value.trim().length >= 3;
});

const isDirty = computed(() => {
  return reason.value && reason.value.trim().length > 0;
});

watch(
  () => props.value,
  (newVal) => {
    if (newVal) {
      // Reset reason when dialog opens
      reason.value = "";
      reasonStatus.value = "Not focused";
    }
  }
);

const handleReasonInput = (value) => {
  reason.value = value;
  if (value && value.trim().length >= 3) {
    reasonStatus.value = "Required";
  } else if (value && value.trim().length > 0) {
    reasonStatus.value = "Required";
  } else {
    reasonStatus.value = "Not focused";
  }
};

const handleOverlayClick = () => {
  handleClose();
};

const handleClose = () => {
  emit("input", false);
  emit("close");
};

const handleRemove = () => {
  if (isValid.value) {
    emit("remove", {
      reason: reason.value.trim(),
    });
  }
};

// Handle ESC key
const handleKeydown = (event) => {
  if (event.key === "Escape" && props.value) {
    handleClose();
  }
};

onMounted(() => {
  document.addEventListener("keydown", handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleKeydown);
});
</script>

<style scoped>
.remove-badge-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.remove-badge-dialog {
  background: white;
  background-image: url("@components/assets/Background_pattern_decorative_new.svg");
  background-repeat: no-repeat;
  background-position: top left;
  border-radius: 10px;
  width: 100%;
  max-width: 523px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0px 8px 8px -4px rgba(16, 24, 40, 0.08),
    0px 20px 24px -4px rgba(16, 24, 40, 0.14);
  position: relative;
}

.modal-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  padding: 24px 24px 0 24px;
  width: 100%;
  z-index: 1;
}

.header-content {
  width: 100%;
}

.featured-icon {
  background: white;
  border: 1px solid #eaecf0;
  border-radius: 10px;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.text-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
  width: 100%;
  padding-top: 16px;
}

.dialog-title {
  font-family: "Inter", sans-serif;
  font-weight: 600;
  font-size: 18px;
  line-height: 28px;
  color: #101828;
  text-align: center;
  width: 100%;
  margin: 0;
}

.close-button {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border-radius: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #98a2b3;
  transition: background-color 0.2s;
}

.close-button:hover {
  background-color: rgba(152, 162, 179, 0.1);
}

.dialog-content {
  padding: 9px 24px;
  min-height: 80px;
  display: flex;
  flex-direction: column;
  gap: 0;
  align-items: flex-start;
  width: 100%;
}

.content-text {
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #475467;
  text-align: center;
  width: 100%;
  margin: 0 0 14px 0;
}

.content-text:last-child {
  margin-bottom: 0;
}

.warning-text {
  margin-top: 0;
}

.reason-input-section {
  padding: 9px 24px;
  min-height: 80px;
  width: 100%;
}

.modal-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  padding-top: 32px;
  width: 100%;
}

.actions-content {
  display: flex;
  gap: 12px;
  align-items: center;
  padding-bottom: 24px !important;
  width: 100%;
}

.actions-right {
  display: flex;
  flex: 1 0 0;
  gap: 12px;
  align-items: center;
  justify-content: stretch;
  min-width: 0;
  width: 100%;
}

.remove-button-full-width {
  width: 100% !important;
  flex: 1 0 0;
  cursor: pointer !important;
}

::v-deep .remove-button-full-width button {
  width: 100% !important;
}
::v-deep .memo_input .main_label.is-required {
  color: #344054 !important;
}
::v-deep .memo_input .main_label .required-asterisk {
  color: #344054 !important;
}
.reason-input-section.input-dirty
  ::v-deep
  .memo-textarea
  .v-input__control
  .v-input__slot {
  background-color: #f2edff !important;
}

.reason-input-section.input-dirty
  ::v-deep
  .memo-textarea
  .v-input__control
  .v-input__slot
  fieldset {
  border-color: #7f56d9 !important;
}
</style>
